// PlateViewer — full-screen-ish PDF viewer for an FAA d-TPP plate.
//
// Strategy: <iframe src="/api/plates/pdf?..."> is the lightest path
// that works on every target surface (desktop Chrome/Safari/Firefox,
// iPad PWA, iPhone PWA). iOS Safari has historically had a wonky
// inline PDF experience inside iframes; the fallback is "open in new
// tab" which the OS handles cleanly via Quick Look.
//
// We deliberately do NOT pull in pdf.js / react-pdf yet — both add
// 500KB+ of JS for a feature where the browser already ships a
// native, GPU-accelerated, AI-typesetting-aware PDF renderer. If
// iPhone's <iframe> rendering proves flaky in production we'll
// revisit (this is a 'wait for production telemetry' decision).
//
// Props:
//   - cycle / pdfName: identifies the plate to render
//   - title / subtitle: header chrome
//   - onClose: dismiss handler
//
// On iOS standalone PWAs, <iframe> may show a black box for PDFs.
// We detect that with a load timeout + a "couldn't render?" CTA that
// opens the PDF in a new tab.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../shell/Icon';
import { platePdfUrl } from '../lib/plates';
import { useUiStore } from '../stores/uiStore';
import { computePlanViewCorners, computeCourseAlignedCorners } from '../lib/plateGeoref';
import type { PlateOverlaySpec, ApproachFix } from '../lib/plateGeoref';

interface Props {
  cycle: string;
  pdfName: string;
  title: string;
  subtitle?: string;
  /** Airport ICAO ident (used to build overlay spec). */
  airportIdent?: string;
  /** Pre-computed corners from CIFP fix data. If not provided, will use
   *  a best-effort bounding box from the demo data. */
  corners?: PlateOverlaySpec['corners'];
  /** CIFP approach fixes for computing corners on-the-fly. */
  approachFixes?: ApproachFix[];
  onClose: () => void;
}

export function PlateViewer({
  cycle,
  pdfName,
  title,
  subtitle,
  airportIdent,
  corners: cornersFromProps,
  approachFixes,
  onClose,
}: Props) {
  const url = platePdfUrl(cycle, pdfName);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [stuck, setStuck] = useState(false);
  const [onMap, setOnMap] = useState(false);

  const navigate = useNavigate();
  // Select scalars individually — NEVER return a fresh object from a Zustand
  // selector (`(s) => ({ a: s.a, b: s.b })`). Zustand defaults to Object.is
  // for equality checks, so a new object identity every render schedules an
  // immediate re-render → infinite loop → React error #185 ("Maximum update
  // depth exceeded") and a blank screen. Reproduced 2026-05-14 night: opening
  // the airport diagram for KSEA bricked the desktop app.
  const activePlateOverlay = useUiStore((s) => s.activePlateOverlay);
  const setPlateOverlay = useUiStore((s) => s.setPlateOverlay);

  // Track whether THIS plate is currently active on the map.
  const isActiveOnMap = activePlateOverlay?.pdfName === pdfName;

  const handleViewOnMap = () => {
    if (isActiveOnMap) {
      // Dismiss the overlay if already active.
      setPlateOverlay(null);
      setOnMap(false);
      return;
    }
    // Compute corners. Prefer course-aligned corners from approachFixes
    // (rotates the plate to match the final approach course); fall back to
    // caller-provided corners; fall back to a north-up bounding box.
    let effectiveCorners = cornersFromProps;
    let courseDegTrue: number | undefined;
    if (approachFixes && approachFixes.length >= 2) {
      try {
        const aligned = computeCourseAlignedCorners(approachFixes, 2, 0.85);
        effectiveCorners = aligned.corners;
        courseDegTrue = aligned.courseDegTrue;
      } catch (err) {
        console.warn('[PlateViewer] course-aligned corners failed; falling back to bbox', err);
        effectiveCorners = effectiveCorners ?? computePlanViewCorners(approachFixes, 3);
      }
    }
    if (!effectiveCorners) {
      // Fallback: a minimal placeholder that won't crash. Pilot can
      // navigate to the right area manually.
      console.warn('[PlateViewer] No corners available for overlay — overlay will be off-position');
      return;
    }
    const spec: PlateOverlaySpec = {
      cycle,
      pdfName,
      chartName: title,
      airportIdent: airportIdent ?? 'UNKN',
      corners: effectiveCorners,
      planViewFraction: 0.62,
      courseDegTrue,
    };
    setPlateOverlay(spec);
    setOnMap(true);
    // Navigate to map so the pilot can see the overlay in context.
    navigate('/map');
  };

  // If the iframe never reports `load` after 6s, surface the
  // "open externally" affordance — iOS standalone PWAs sometimes
  // refuse to inline PDFs at all.
  //
  // HI-4 audit 2026-05-22: drop the timeout to 1.5 s when offline so
  // the pilot sees an honest "Offline — last cached version may not
  // be available" CTA almost immediately, instead of staring at a
  // spinner labelled "Loading…" while the SW serves a cache miss.
  useEffect(() => {
    const offline = typeof navigator !== 'undefined' && !navigator.onLine;
    const ms = offline ? 1500 : 6000;
    const t = window.setTimeout(() => {
      if (!loaded) setStuck(true);
    }, ms);
    return () => window.clearTimeout(t);
  }, [loaded]);

  return (
    <div
      className="absolute inset-0 z-50 bg-cockpit-900 flex flex-col"
      role="dialog"
      aria-label={`Plate viewer: ${title}`}
    >
      <header
        className="shrink-0 border-b border-cockpit-700 bg-cockpit-800/95 backdrop-blur"
        style={{ paddingTop: 'var(--safe-top)' }}
      >
        <div className="flex items-center px-3 py-2 gap-2">
          <button
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center rounded text-cockpit-200 hover:text-cockpit-50 hover:bg-cockpit-700"
            aria-label="Close plate"
            title="Close"
          >
            <Icon name="x" className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-cockpit-100 truncate">{title}</div>
            {subtitle && <div className="text-[11px] text-cockpit-300 truncate">{subtitle}</div>}
          </div>
          {/* View on Map — shown when geo-reference data is available */}
          {(cornersFromProps ?? approachFixes) && (
            <button
              onClick={handleViewOnMap}
              className={[
                'text-[11px] px-2 py-1 rounded flex items-center gap-1',
                isActiveOnMap
                  ? 'bg-ember-600 text-white'
                  : 'bg-cockpit-700 text-cockpit-100 hover:bg-cockpit-600',
              ].join(' ')}
              title={isActiveOnMap ? 'Remove from map' : 'View plan on moving map'}
            >
              <Icon name="map" className="h-3.5 w-3.5" />
              {isActiveOnMap ? 'On Map ✕' : 'Map'}
            </button>
          )}
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] px-2 py-1 rounded bg-cockpit-700 text-cockpit-100 hover:bg-cockpit-600"
            title="Open in new tab"
          >
            Open ↗
          </a>
        </div>
      </header>
      <div className="flex-1 relative bg-white">
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-cockpit-700 bg-cockpit-100">
            {typeof navigator !== 'undefined' && !navigator.onLine
              ? `Offline — trying cached ${pdfName}…`
              : `Loading ${pdfName}…`}
          </div>
        )}
        {stuck && !loaded && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-sm bg-cockpit-100 text-cockpit-800 px-6 text-center gap-3">
            {typeof navigator !== 'undefined' && !navigator.onLine ? (
              <>
                <p>You’re offline and this plate isn’t cached.</p>
                <p className="text-xs text-cockpit-600">
                  Plates you’ve viewed before stay available offline; this
                  one will load when signal returns.
                </p>
              </>
            ) : (
              <>
                <p>iPhone PWAs sometimes refuse to inline PDFs.</p>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-ember-600 text-white px-3 py-1.5 rounded text-xs"
                >
                  Open {pdfName} in a new tab
                </a>
              </>
            )}
          </div>
        )}
        {/* iframe is the actual viewer — keep it mounted even after the
            stuck overlay shows, in case it recovers. */}
        <iframe
          ref={iframeRef}
          src={url}
          title={title}
          className="absolute inset-0 w-full h-full border-0 bg-white"
          onLoad={() => setLoaded(true)}
        />
      </div>
    </div>
  );
}
